import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from '@/store';
import { canAccessPage as checkCanAccessPage } from '@/components/acl/can-access-page';

const enum AclAccessLevelEnum {
  deny = 'deny',
  reportsOnly = 'reportsOnly',
  readOnly = 'readOnly',
  fullAccess = 'fullAccess',
}

const enum AclRole {
  user = 'user',
  member = 'member',
}

const enum AclMemberRole {
  memberUser = 'memberUser',
  memberAdmin = 'memberAdmin',
}

export const useAcl = () => {
  const route = useRoute();
  const store = useStore();

  const reportTransform: { [type: string]: { single: string; plural: string } } = {
    landings: {
      single: 'landing',
      plural: 'landings',
    },
    campaigns: {
      single: 'campaign',
      plural: 'campaigns',
    },
    offers: {
      single: 'offer',
      plural: 'offers',
    },
    flows: {
      single: 'flow',
      plural: 'flows',
    },
    'traffic-sources': {
      single: 'trafficSource',
      plural: 'trafficSources',
    },
    'affiliate-networks': {
      single: 'affiliateNetwork',
      plural: 'affiliateNetworks',
    },
  };

  const routeType = computed(() => route.params.type);

  const isReport = computed(() => !!route.params.report);

  const userRole = computed(() =>
    store.user?.isMember ? AclRole.member : AclRole.user,
  );

  const isMemberRoleUser = computed(() => store.user?.role === 'user');

  const memberRole = computed(() => {
    if (store.user.role === 'user') {
      return AclMemberRole.memberUser;
    }
    if (store.user.role === 'admin') {
      return AclMemberRole.memberAdmin;
    }
    return false;
  });

  const isUserOrMemberAdmin = computed(
    () =>
      !store.user.isMember ||
      (store.user.isMember && store.member && store.member.role === 'admin'),
  );

  const getAclSection = ({
    single = true,
    type = routeType.value,
  }: { single?: boolean; type?: string } = {}): string => {
    let result = 'default';

    if (isReport.value) {
      const reportTransformForType = reportTransform[type as string];
      if (reportTransformForType) {
        result = single ? reportTransformForType.single : reportTransformForType.plural;
      }
    }

    return result;
  };

  const acl = ({ workspaceId, type }: { workspaceId: string; type: string }) => {
    const _default = { '*': AclAccessLevelEnum.deny };
    if (!workspaceId) return _default;

    const aclSection = getAclSection({ type });
    const workspaceAcl = store.acl[workspaceId].acl;
    return workspaceAcl[aclSection] ? workspaceAcl[aclSection] : _default;
  };

  const aclGetEditItemRights = (data: any): string => {
    const { workspaceId, id } = data;
    if (!workspaceId || !id) return;

    if (
      !isMemberRoleUser.value ||
      store.acl[workspaceId].accessToExistingItems === AclAccessLevelEnum.fullAccess
    ) {
      return AclAccessLevelEnum.fullAccess;
    }

    const aclWorkspace = acl(data);
    return aclWorkspace[id] ? aclWorkspace[id] : aclWorkspace['*'];
  };

  const aclCanEditItem = (data: any): boolean => {
    return aclGetEditItemRights(data) === AclAccessLevelEnum.fullAccess;
  };

  const aclReadOnly = (data: any): boolean => {
    const { workspaceId, id, type } = data;
    if (!isMemberRoleUser.value || !workspaceId || !id) return;

    if (
      store.acl[workspaceId].accessToExistingItems === AclAccessLevelEnum.fullAccess
    )
      return;

    const aclWorkspace = acl({ workspaceId, type });
    return (
      isMemberRoleUser.value &&
      (aclWorkspace[id] || aclWorkspace['*']) === AclAccessLevelEnum.readOnly
    );
  };

  const aclCanViewItem = (data: any): boolean => {
    return (
      aclGetEditItemRights(data) === AclAccessLevelEnum.fullAccess ||
      aclGetEditItemRights(data) === AclAccessLevelEnum.readOnly
    );
  };

  const aclCanCreate = computed(() => {
    return (
      !isMemberRoleUser.value ||
      store.user.canCreate[getAclSection({ single: false })]
    );
  });

  const aclCanCreateOnSection = ({
    workspaceId,
    section,
  }: {
    workspaceId: string;
    section?: string;
  }): boolean => {
    if (!workspaceId) return;
    if (!isMemberRoleUser.value) return true;

    const aclSection = section || getAclSection({ single: false });
    const workspaceAcl = store.acl && store.acl[workspaceId];
    return workspaceAcl && workspaceAcl.canCreate[aclSection];
  };

  const selectOnlyPrivateWorkspaces = computed(() => {
    switch (getAclSection()) {
      case 'campaign':
        return true;
      case 'affiliateNetwork':
      case 'flow':
      case 'landing':
      case 'offer':
      case 'trafficSource':
        return isMemberRoleUser.value;
      default:
        return false;
    }
  });

  const canAccessPage = checkCanAccessPage;

  return {
    reportTransform,
    isReport,
    userRole,
    isMemberRoleUser,
    memberRole,
    isUserOrMemberAdmin,
    aclCanCreate,
    getAclSection,
    acl,
    aclGetEditItemRights,
    aclCanEditItem,
    aclReadOnly,
    aclCanViewItem,
    aclCanCreateOnSection,
    selectOnlyPrivateWorkspaces,
    canAccessPage,
  };
};
